import { type ComponentProps, useId } from 'react'

import { Input } from '@/components/ui/input'
import type { SearchableSelectOption } from '@/components/ui/searchable-select'
import { formatDate } from '../../../../lib/utils'
import { ClaimDriverRelationship, ClaimTrailerOwnership } from '../../../../models/claim'
import type { Driver } from '../../../../models/driver'
import type { Equipment } from '../../../../models/equipment'

export const OWNERSHIP_OPTIONS: { value: ClaimTrailerOwnership; label: string }[] = [
  { value: ClaimTrailerOwnership.OWNED_NOT_LISTED, label: 'We own it — not yet listed on policy' },
  { value: ClaimTrailerOwnership.LEASED, label: 'Leased' },
  { value: ClaimTrailerOwnership.THIRD_PARTY_CARRIER, label: 'Third-party / Carrier-provided' },
  { value: ClaimTrailerOwnership.UNKNOWN, label: 'Unknown' },
]

export const RELATIONSHIP_OPTIONS: { value: ClaimDriverRelationship; label: string }[] = [
  { value: ClaimDriverRelationship.EMPLOYEE, label: 'Employee' },
  { value: ClaimDriverRelationship.OWNER_INSURED, label: 'Owner / Insured' },
  { value: ClaimDriverRelationship.CONTRACTOR, label: 'Contractor' },
  { value: ClaimDriverRelationship.OTHER, label: 'Other' },
]

/** "2020 Kenworth T680", with the VIN and plate underneath to tell units apart. */
export function equipmentOption(e: Equipment): SearchableSelectOption {
  const desc = [e.year, e.make, e.model].filter(Boolean).join(' ')
  const [label, ...details] = [
    desc,
    e.vin && `VIN ${e.vin}`,
    e.licensePlate && `Plate ${e.licensePlate}`,
  ].filter(Boolean)
  return {
    value: e.id,
    label: label || 'Unit without details',
    description: details.join(' · ') || undefined,
    // Hidden, but a fleet that knows its units by number can still type one.
    keywords: e.vehicleNumber ? [e.vehicleNumber] : undefined,
  }
}

/** The driver's name, with date of birth and the license's last four for same-named drivers. */
export function driverOption(d: Driver): SearchableSelectOption {
  const license = d.licenseNumber
    ? `CDL ••${d.licenseNumber.slice(-4)}${d.licenseState ? ` (${d.licenseState})` : ''}`
    : ''
  const description = [d.dob && `DOB ${formatDate(d.dob)}`, license].filter(Boolean).join(' · ')
  return {
    value: d.id,
    label: `${d.firstName} ${d.lastName}`.trim() || 'Driver without a name',
    description: description || undefined,
    keywords: d.licenseNumber ? [d.licenseNumber] : undefined,
  }
}

/**
 * Pre-filled from the policy record and still typeable: a fleet row with a missing
 * plate otherwise left the reporter staring at a box they could not complete.
 */
export function PrefilledField({
  label,
  placeholder,
  id,
  ...props
}: { label: string; placeholder?: string } & Omit<ComponentProps<'input'>, 'size'>) {
  const generated = useId()
  const inputId = id ?? generated
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-neutral-700">
        {label} <span className="pl-2 text-xs font-normal text-neutral-400">From your policy</span>
      </label>
      <Input id={inputId} placeholder={placeholder ?? 'Add it if we have it wrong'} {...props} />
    </div>
  )
}
